export enum FigureVarType {
  list = "list",
  number = "number",
  string = "string",
  select = "select",
  variable = "variable",
}

/**
 * Defines conditional requirements for a variable setting.
 * This determines when a particular variable setting should be displayed or enabled.
 */
export interface FigureVarRequires {
  /** The name of the variable that must match */
  varName: string;
  /** Collection of values that the variable must have for this setting to be applicable */
  values: string[];
}

export interface FigureVarSelectSettings {
  type: FigureVarType.select;
  defaultValue?: string;
  label: string;
  options: string[];
  varName: string;
  requires?: FigureVarRequires;
}

export interface FigureVarStandardSettings {
  type: FigureVarType.list | FigureVarType.number | FigureVarType.string | FigureVarType.variable;
  defaultValue?: string;
  label: string;
  varName: string;
  requires?: FigureVarRequires;
}

export type FigureVarSettings = FigureVarSelectSettings | FigureVarStandardSettings;

/**
 * Type guard to check if an object is a FigureVarSettings
 *
 * @param obj - The object to check
 * @returns - true if the object is a FigureVarSettings
 */
export const isFigureVarSettings = (obj: unknown): obj is FigureVarSettings => {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }
  const chkObj = obj as FigureVarSettings;
  if (typeof chkObj.varName !== "string") {
    return false;
  }
  if (typeof chkObj.label !== "string") {
    return false;
  }
  if (
    chkObj.type !== "list" &&
    chkObj.type !== "number" &&
    chkObj.type !== "string" &&
    chkObj.type !== "select" &&
    chkObj.type !== "variable"
  ) {
    return false;
  }
  if (chkObj.type === FigureVarType.select) {
    const selectObj = chkObj as FigureVarSelectSettings;
    if (
      !Array.isArray(selectObj.options) ||
      selectObj.options.some((opt) => typeof opt !== "string")
    ) {
      return false;
    }
  }
  if (chkObj.type !== FigureVarType.select && "options" in chkObj) {
    return false;
  }
  if ("requires" in chkObj && chkObj.requires !== undefined) {
    if (typeof chkObj.requires !== "object" || chkObj.requires === null) {
      return false;
    }
    const requires = chkObj.requires as FigureVarRequires;
    if (typeof requires.varName !== "string") {
      return false;
    }
    if (!Array.isArray(requires.values) || requires.values.some((val) => typeof val !== "string")) {
      return false;
    }
  }
  return true;
};
